import React from 'react';
import { MailIcon, PhoneIcon, MapPinIcon, GlobeIcon, LinkedinIcon, GithubIcon } from 'lucide-react';
import cvData from '../../data/cv';
/** Editorial masthead: oversized serif name, kicker, contacts and stats. */
export function Masthead({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    profile,
    stats,
    meta,
    experience,
    earlierExperience
  } = cvData;
  // Career length is data-driven: prefer the explicit careerStartYear, else the
  // earliest year found across ALL experience (detailed + earlier roles).
  const earliestYear = profile.careerStartYear ?? [...experience, ...earlierExperience].flatMap(e => e.period.match(/\d{4}/g) ?? []).map(Number).filter(Boolean).sort((a, b) => a - b)[0];
  const years = earliestYear ? new Date().getFullYear() - earliestYear : 15;
  const [first, ...rest] = profile.name.split(' ');
  const last = rest.join(' ');
  return <header className="relative" data-id="element-139" data-mp="fg7e7h71tbxi">
      {/* top meta rail */}
      <div className="flex items-center justify-between border-b border-[var(--line-strong)] pb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--ink-faint)]" data-id="element-140" data-mp="shr81wgnow4j">
        <span data-id="element-141" data-mp="np7vn2tpg71b">{meta.documentLabel}</span>
        <span className="tnum" data-id="element-142" data-mp="irodvsxstc4m">{`≈ ${years} yrs · ${meta.homeCity}`}</span>
      </div>

      <div className={compact ? 'pt-4' : 'pt-8'} data-id="element-143" data-mp="dteltr7dern8">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[var(--accent)]" data-id="element-144" data-mp="dupm3h1lxh9l">
          {meta.kicker}
        </p>

        {/* name + contacts share the row so the wide right space is used */}
        <div className={`${compact ? 'mt-2' : 'mt-4'} flex items-start justify-between gap-8`} data-id="element-145" data-mp="16illitik9na2">
          <h1 className={`font-display display-tight font-light leading-[0.9] text-[var(--ink)] ${compact ? 'text-[48px]' : 'text-[76px]'}`} data-id="element-146" data-mp="11kk1a59efhx0">
            {first}
            {last && (compact ? <span className="italic" data-id="element-147" data-mp="3i73938f618i6"> {last}</span> : <>
                  <br data-id="element-148" data-mp="3jo8nqmb0d30b" />
                  <span className="italic" data-id="element-149" data-mp="3jptiabxboly6">{last}</span>
                </>)}
          </h1>

          <address className={`shrink-0 not-italic font-mono text-[11px] text-[var(--ink-soft)] ${compact ? 'grid grid-cols-2 gap-x-6 gap-y-1 pt-1' : 'space-y-1.5 pt-1'}`} data-id="element-150" data-mp="3on34lipf0hhs">
            <a href={`mailto:${profile.email}`} className="flex items-center gap-2 hover:text-[var(--accent)]" data-id="element-151" data-mp="3eo49l2xkokd9">
              <MailIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-152" data-mp="35oh4o75mwwfg" />
              {profile.email}
            </a>
            <a href={`tel:${profile.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 tnum hover:text-[var(--accent)]" data-id="element-153" data-mp="2qy03mowww8j3">
              <PhoneIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-154" data-mp="2hzy6ikgau013" />
              {profile.phone}
            </a>
            {profile.website && <a href={`https://${profile.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[var(--accent)]" data-id="element-155" data-mp="23ealyy66othp">
                <GlobeIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-156" data-mp="27djfc1xbapcq" />
                {profile.website}
              </a>}
            {profile.linkedin && <a href={`https://${profile.linkedin}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[var(--accent)]" data-id="element-157" data-mp="2hbpck686b7b9">
                <LinkedinIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-158" data-mp="351t5njnqjoo0" />
                {profile.linkedin.replace('linkedin.com/in/', '@')}
              </a>}
            {profile.github && <a href={`https://${profile.github}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[var(--accent)]" data-id="element-159" data-mp="3k2qlq5loval9">
                <GithubIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-160" data-mp="1qkcow7jtrb1" />
                {profile.github.replace('github.com/', '@')}
              </a>}
            <span className="flex items-center gap-2 text-[var(--ink-faint)]" data-id="element-161" data-mp="358by1rr6jd09">
              <MapPinIcon className="h-3.5 w-3.5 text-[var(--accent)]" aria-hidden="true" data-id="element-162" data-mp="3a6drn6m8eupz" />
              {profile.location}
            </span>
          </address>
        </div>

        {/* headline stats */}
        {compact ?
      // Condensed: a tight single-line stat strip — high signal, low height.
      <dl className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t border-[var(--line)] pt-3" data-id="element-163" data-mp="uhc47gdo5rs1">
            {stats.map(s => <div key={s.label} className="flex items-baseline gap-1.5" data-id="element-164" data-mp="29zi3ra2ym6za">
                <dt className="sr-only" data-id="element-165" data-mp="257gwk2knf6g4">{s.label}</dt>
                <dd className="font-display text-[17px] font-medium leading-none text-[var(--ink)]" data-id="element-166" data-mp="255xf48n9apcr">
                  {s.value}
                </dd>
                <span className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-[var(--ink-faint)]" data-id="element-167" data-mp="1w7lfxi8bqj8p">
                  {s.label}
                </span>
              </div>)}
          </dl> : <dl className="mt-7 grid grid-cols-4 gap-6 border-t border-[var(--line)] pt-5" data-id="element-168" data-mp="1m9xhoibietls">
            {stats.map(s => <div key={s.label} data-id="element-169" data-mp="1hbm9ec0v4r5o">
                <dt className="sr-only" data-id="element-170" data-mp="3gmx2qqynsg0g">{s.label}</dt>
                <dd className="font-display text-[30px] font-light leading-none text-[var(--ink)]" data-id="element-171" data-mp="3gmdn5gwhe3pl">
                  {s.value}
                </dd>
                <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--ink-faint)]" data-id="element-172" data-mp="19ywu7e11ahnh">
                  {s.label}
                </p>
              </div>)}
          </dl>}
      </div>
    </header>;
}